import React, { useEffect, useState } from 'react';
import { useFormik } from 'formik';
import { useParams, useLocation } from 'react-router-dom';
import {
  TextField,
  MenuItem,
  FormGroup,
  Grid,
  DialogActions,
  Button,
} from '@mui/material';
import * as Yup from 'yup';
import NumberFormat from 'react-number-format';
import { useDispatch } from 'react-redux';
import { setLoading } from 'redux/loading';
import { TooltipMain } from 'components/tooltips/tooltipsMain';
import Success from 'components/Snack/Success';
import Error from 'components/Snack/Error';
import AlertDialog from 'components/Dialogs/dialogMain';
import api from 'services/api';
import { setDisable } from 'redux/disable';
import DialogIfExistsPond from 'pages/pond/DialogIfExistsPond';

function Pond({ setOpenPondModal, setOpenAutocomplete, setOpenLinearProgress }) {
  const [property, setProperty] = useState(null);
  const [showAlertDialog, setShowAlertDialog] = useState(false);
  const [hasPondWithThisDescription, setHasPondWithThisDescription] = useState(false);
  const [openDialogPondExists, setDialogPondExists] = useState(false);
  const [messageError, setMessageError] = useState('');
  const [thereIsPond, setThereIsPond] = useState(false);

  const location = useLocation();
  const dispatch = useDispatch();
  let { id } = useParams();
  const error = Error();
  const success = Success();

  useEffect(() => {
    if (location.state !== undefined) {
      if (location.state.customData) {
        setData(location.state.customData);
        formik.setFieldValue('id', null);
      } else if (location.state.data) {
        id = location.state.id;
        setData(location.state.data);
      }
    }
  }, [location]);

  function setData(data) {
    formik.setValues(data);
  }

  async function request(data) {
    // envia os dados para api salvar no banco
    dispatch(setDisable(true));
    try {
      await api.post('/ponds', { ...data }).then((resp) => {
        const pondOBJ = {
          capacity: resp.data.capacity,
          description: resp.data.description,
          id: resp.data.id,
          protocol: resp.data.protocol,
          status: resp.data.status,
          system: resp.data.system,
          type: resp.data.type,
        };
        setOpenLinearProgress(false);
        setOpenPondModal(false);
        setOpenAutocomplete(true);
      });
      success();
    } catch (err) {
      if (err.response.data.statusCode === 400) {
        if (openDialogPondExists === true) {
          setDialogPondExists(false);
        }
        setDialogPondExists(true);
        setMessageError(err.response.data.message);
      }
      error();
    } finally {
      dispatch(setDisable(false));
      dispatch(setLoading(false));
    }
  }

  // Criação dos campos e validação para o formulário
  const formik = useFormik({
    initialValues: {
      property_id: '',
      capacity: '',
      description: '',
      type: '',
      status: '',
      system: '',
      protocol: '',
    },
    validationSchema: Yup.object({
      status: Yup.string().required('Campo Obrigatório'),
      description: Yup.string().max(50, 'Máximo de caracteres (50 caracteres).').required('Campo Obrigatório'),
      capacity: Yup.number().max(99999, 'Limite ultrapassado (5 números).').required('Campo Obrigatório').min(0, 'Precisa ser acima de 0'),
      type: Yup.string().required('Campo Obrigatório'),
      system: Yup.string().required('Campo Obrigatório'),
      protocol: Yup.string().required('Campo Obrigatório'),
    }),
    onSubmit: async (values) => {
      if (!hasPondWithThisDescription) {
        setOpenLinearProgress(true);
        dispatch(setLoading(true));
        values.property_id = property.id;
        request(values);
      } else {
        setShowAlertDialog(true);
      }
    },
  });

  useEffect(() => {
    async function load() {
      // carrega os dados no seu devido campo caso tenha id, do contrário,
      // carrega o formulário vazio para cadastrar um novo
      global.loading = true;
      const resp = await api.get('/properties');

      const pathname = window.location.pathname
        .toString()
        .replace(/\D/g, '');
      const pond = await api.get('/ponds/');

      // Se existe um id o forEach vai checar todos os IDs de pond que vem
      // do back (pond.data.id) pra ver qual é igual ao id da requisição (pathname)
      pond.data.forEach((data) => {
        if (data.id === pathname) {
          formik.setValues(data);
          setThereIsPond(true);
        }
      });

      if (resp.data) {
        setProperty(resp.data);
      }

      global.loading = false;
    }
    load();
  }, []);

  async function hasPond(description) {
    setTimeout(async () => {
      const { data } = await api.get(
        `/ponds/description/${description.trim()}`,
      );

      return data
        ? setHasPondWithThisDescription(true)
        : setHasPondWithThisDescription(false);
    }, 200);
  }

  return (
    <div>
      {openDialogPondExists && <DialogIfExistsPond message={messageError} />}
      <AlertDialog
        open={showAlertDialog}
        setOpen={setShowAlertDialog}
        positiveCall={() => {
          setShowAlertDialog(false);
        }}
        route="/cycles"
        message="Cadastro não efetuado. Existe um viveiro cadastrado com esse nome. "
        text="Falha"
        button="OK"
        noCancel
      />
      <form
        style={{ padding: '1rem' }}
        onSubmit={formik.handleSubmit}
      >
        <Grid container spacing={2}>
          <Grid item xs={12} sm={8}>
            <FormGroup>
              <TextField
                inputProps={{ maxLength: 51 }}
                InputLabelProps={{ style: { pointerEvents: 'auto' } }}
                id="description"
                label={<div>Nome</div>}
                variant="outlined"
                name="description"
                type="text"
                value={formik.values.description}
                onChange={(event) => {
                  hasPond(event.target.value);
                  formik.setFieldValue('description', event.target.value);
                }}
                onBlur={() => hasPond(formik.values.description)}
                error={
                  formik.touched.description && formik.errors.description
                }
                helperText={
                  formik.touched.description && formik.errors.description
                }
                disabled={formik.isSubmitting}
              />
            </FormGroup>
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormGroup>
              <TextField
                InputLabelProps={{ style: { pointerEvents: 'auto' } }}
                id="status"
                select
                label={<div>Status do viveiro</div>}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                variant="outlined"
                value={formik.values.status}
                name="status"
                error={!!(formik.touched.status && formik.errors.status)}
                helperText={formik.touched.status && formik.errors.status}
                disabled={formik.isSubmitting}
              >
                <MenuItem key={2} value="ativo">
                  Ativo
                </MenuItem>
                <MenuItem key={3} value="inativo">
                  Inativo
                </MenuItem>
              </TextField>
            </FormGroup>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormGroup>
              <NumberFormat
                inputProps={{ maxLength: 7 }}
                InputLabelProps={{ style: { pointerEvents: 'auto' } }}
                id="capacity"
                label={<div> Área (m²) </div>}
                variant="outlined"
                name="capacity"
                thousandSeparator="."
                decimalSeparator=","
                decimalScale={2}
                value={formik.values.capacity}
                onValueChange={(values) => {
                  formik.setValues({
                    ...formik.values,
                    capacity: values.floatValue,
                  });
                }}
                onBlur={formik.handleBlur}
                customInput={TextField}
                error={!!(formik.touched.capacity && formik.errors.capacity)}
                helperText={
                  formik.touched.capacity && formik.errors.capacity
                }
                disabled={formik.isSubmitting}
              />
            </FormGroup>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormGroup>
              <TextField
                InputLabelProps={{ style: { pointerEvents: 'auto' } }}
                id="type"
                select
                label={(
                  <div>
                    Tipo
                    {' '}
                    <TooltipMain
                      message="Escolha o tipo de viveiro que se adéqua ao que está sendo criado"
                    />
                  </div>
                )}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                variant="outlined"
                value={formik.values.type}
                name="type"
                error={!!(formik.touched.type && formik.errors.type)}
                helperText={formik.touched.type && formik.errors.type}
                disabled={formik.isSubmitting}
              >
                <MenuItem key={1} value="Pré-Berçário">
                  Pré-Berçário
                </MenuItem>
                <MenuItem key={2} value="Berçário">
                  Berçário
                </MenuItem>
                <MenuItem key={3} value="Engorda">
                  Engorda
                </MenuItem>
                <MenuItem key={4} value="Tanque-Rede">
                  Tanque rede
                </MenuItem>
                {/* <MenuItem key={5} value="Outros">
                  Outros
                </MenuItem> */}
              </TextField>
            </FormGroup>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormGroup>
              <TextField
                InputLabelProps={{ style: { pointerEvents: 'auto' } }}
                id="system"
                select
                label={(
                  <div>
                    Sistema de produção
                    <TooltipMain
                      message="Tipo de sistema de produção utilizado"
                    />
                  </div>
                )}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                variant="outlined"
                value={formik.values.system}
                name="system"
                error={!!(formik.touched.system && formik.errors.system)}
                helperText={formik.touched.system && formik.errors.system}
              >
                <MenuItem key={1} value="Semi-Extensivo">
                  Semi-Extensivo
                </MenuItem>
                <MenuItem key={2} value="Extensivo">
                  Extensivo
                </MenuItem>
                <MenuItem key={3} value="Semi-Intensivo">
                  Semi-Intensivo
                </MenuItem>
                <MenuItem key={4} value="Intensivo">
                  Intensivo
                </MenuItem>
                <MenuItem key={5} value="Superintensivo">
                  Superintensivo
                </MenuItem>
              </TextField>
            </FormGroup>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormGroup>
              <TextField
                InputLabelProps={{ style: { pointerEvents: 'auto' } }}
                id="protocol"
                select
                label={(
                  <div>
                    Protocolo de trabalho
                    <TooltipMain
                      message="Protocolo usado no sistema de produção (Opcional)"
                    />
                  </div>
                )}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                variant="outlined"
                value={formik.values.protocol}
                name="protocol"
                error={!!(formik.touched.protocol && formik.errors.protocol)}
                helperText={
                  formik.touched.protocol && formik.errors.protocol
                }
              >
                <MenuItem key={1} value="Bioflocos">
                  Bioflocos
                </MenuItem>
                <MenuItem key={2} value="Heterotróficos">
                  Heterotróficos
                </MenuItem>
                <MenuItem key={3} value="Simbióticos">
                  Simbióticos
                </MenuItem>
                <MenuItem key={4} value="Outro">
                  Outro
                </MenuItem>
              </TextField>
            </FormGroup>
          </Grid>
        </Grid>

        <DialogActions style={{ marginTop: '1.5rem', padding: '0px' }}>
          <Button
            variant="outlined"
            onClick={() => setOpenPondModal(false)}
            disabled={formik.isSubmitting}
            style={{
              border: '1px solid red',
              color: 'red',
            }}
          >
            Cancelar
          </Button>
          <Button
            onClick={formik.handleSubmit}
            disabled={formik.isSubmitting}
            style={{
              backgroundColor: '#0267C1',
              color: 'white',
            }}
          >
            Adicionar
          </Button>
        </DialogActions>
      </form>
    </div>
  );
}

export default Pond;
